import React from "react";
import ReactDOM from "react-dom";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store/store";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "./index.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ToggleProvider } from "./hooks/useToggle";
import { NotificationProvider } from "./contexts/NotificationContext";
import { initializeSessionValues } from "./utils/initSession";
import { startVersionCheck } from "./utils/versionCheck";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 1,
      retry: 2,
      cacheTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

async function bootstrap() {
  // Zombie service worker cleanup — safe no-op when no SWs are registered.
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => regs.forEach((r) => r.unregister()))
      .catch(() => {});
  }

  // Initialize session values BEFORE rendering the App
  await initializeSessionValues();

  const tree = (
    <ToggleProvider>
      <NotificationProvider>
        <App />
      </NotificationProvider>
    </ToggleProvider>
  );

  ReactDOM.render(
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          {GOOGLE_CLIENT_ID ? (
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              {tree}
            </GoogleOAuthProvider>
          ) : (
            tree
          )}
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>,
    document.getElementById("root")
  );

  startVersionCheck();
}

bootstrap();

reportWebVitals();
